import React from 'react';
import moment from 'moment-timezone';
import { useIntl, FormattedMessage } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import { convertMoneyToNumber, formatMoney } from '../../util/currency';
import { timestampToDate } from '../../util/dates';
import { hasPermissionToInitiateTransactions, isUserAuthorized } from '../../util/userHelpers';
import {
  NO_ACCESS_PAGE_INITIATE_TRANSACTIONS,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  createSlug,
} from '../../util/urlHelpers';
import { sendLeadRequest, isLeadProcessListing } from './ListingPage.duck';

import { Page, LayoutSingleColumn } from '../../components';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import css from './ListingPage.module.css';
import { calculatePetCounts } from '../../util/bookingHelper';

/**
 * This file contains shared functions from each ListingPage variants.
 */

const { UUID, Money } = sdkTypes;

/**
 * Helper to get formattedPrice and priceTitle for SectionHeading component.
 * @param {Money} price listing's price
 * @param {String} marketplaceCurrency currency of the price (e.g. 'USD')
 * @param {Object} intl React Intl instance
 * @returns Object literal containing formattedPrice and priceTitle
 */
export const priceData = (price, marketplaceCurrency, intl) => {
  if (price && price.currency === marketplaceCurrency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

/**
 * Converts Money object to number, which is needed for the search schema (for Google etc.)
 *
 * @param {Money} price
 * @returns {Money|null}
 */
export const priceForSchemaMaybe = price => {
  try {
    const schemaPrice = convertMoneyToNumber(price);
    return {
      price: schemaPrice.toFixed(2),
      priceCurrency: price.currency,
    };
  } catch (e) {
    return {};
  }
};

/**
 * Get category's label.
 *
 * @param {Array} categories array of category objects (key & label)
 * @param {String} value selected category value
 * @returns label for the selected value
 */
export const categoryLabel = (categories, value) => {
  const cat = categories.find(c => c.key === value);
  return cat ? cat.label : value;
};

/**
 * Filter listing images with correct custom image variant name.
 * Used for facebook, twitter and page schema images.
 *
 * @param {Listing} listing
 * @param {String} variantName
 * @returns correct image variant specified by variantName parameter.
 */
export const listingImages = (listing, variantName) =>
  (listing.images || [])
    .map(image => {
      const variants = image.attributes.variants;
      const variant = variants ? variants[variantName] : null;

      // deprecated
      // for backwards combatility only
      const sizes = image.attributes.sizes;
      const size = sizes ? sizes.find(i => i.name === variantName) : null;

      return variant || size;
    })
    .filter(variant => variant != null);

/**
 * Callback for the "contact" button on ListingPage to open inquiry modal.
 *
 * @param {Object} parameters all the info needed to open inquiry modal.
 */
export const handleContactUser = parameters => () => {
  const {
    history,
    params,
    currentUser,
    callSetInitialValues,
    location,
    routes,
    setInitialValues,
    setInquiryModalOpen,
  } = parameters;

  if (!currentUser) {
    const state = { from: `${location.pathname}${location.search}${location.hash}` };

    // We need to log in before showing the modal, but first we need to ensure
    // that modal does open when user is redirected back to this listingpage
    callSetInitialValues(setInitialValues, { inquiryModalOpenForListingId: params.id });

    // signup and return back to listingPage.
    history.push(createResourceLocatorString('SignupPage', routes, {}, {}), state);
  } else if (!isUserAuthorized(currentUser)) {
    // A user in pending-approval state can't contact the author (the same applies for a banned user)
    const pathParams = { missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL };
    history.push(createResourceLocatorString('NoAccessPage', routes, pathParams, {}));
  } else if (!hasPermissionToInitiateTransactions(currentUser)) {
    // A user in pending-approval state can't contact the author (the same applies for a banned user)
    const pathParams = { missingAccessRight: NO_ACCESS_PAGE_INITIATE_TRANSACTIONS };
    history.push(createResourceLocatorString('NoAccessPage', routes, pathParams, {}));
  } else {
    setInquiryModalOpen(true);
  }
};

/**
 * Callback for the inquiry modal to submit aka create inquiry transaction on ListingPage.
 * Note: this is for booking and purchase processes. Inquiry process is handled through handleSubmit.
 *
 * @param {Object} parameters all the info needed to create inquiry.
 */
export const handleSubmitInquiry = parameters => values => {
  const { history, params, getListing, onSendInquiry, routes, setInquiryModalOpen } = parameters;

  const listingId = new UUID(params.id);
  const listing = getListing(listingId);
  const { message } = values;

  onSendInquiry(listing, message.trim())
    .then(txId => {
      setInquiryModalOpen(false);

      // Redirect to OrderDetailsPage
      history.push(createResourceLocatorString('OrderDetailsPage', routes, { id: txId.uuid }, {}));
    })
    .catch(() => {
      // Ignore, error handling in duck file
    });
};

/**
 * Handle order submit from OrderPanel.
 *
 * @param {Object} parameters all the info needed to redirect user to CheckoutPage.
 */
export const handleSubmit = parameters => values => {
  const {
    history,
    params,
    currentUser,
    getListing,
    callSetInitialValues,
    onInitializeCardPaymentData,
    onSendLeadRequest,
    routes,
    lineItems,
    intl,
  } = parameters;
  const listingId = new UUID(params.id);
  const listing = getListing(listingId);

  const {
    bookingDates,
    bookingStartTime,
    bookingEndTime,
    bookingStartDate, // not relevant (omit)
    bookingEndDate, // not relevant (omit)
    priceVariantName, // relevant for bookings
    quantity: quantityRaw,
    seats: seatsRaw,
    deliveryMethod,
    ...otherOrderData
  } = values;

  const bookingMaybe = bookingDates
    ? {
        bookingDates: {
          bookingStart: bookingDates.startDate,
          bookingEnd: bookingDates.endDate,
        },
      }
    : bookingStartTime && bookingEndTime
    ? {
        bookingDates: {
          bookingStart: timestampToDate(bookingStartTime),
          bookingEnd: timestampToDate(bookingEndTime),
        },
      }
    : {};

  // Check if this listing supports lead requests
  if (isLeadProcessListing(listing)) {
    // Handle LEAD REQUEST flow
    const {
      message,
      location,
    } = values || {};

    let onlyOneDay = false;
    const pets = calculatePetCounts(currentUser.pets);
    const petText = Array.isArray(currentUser.pets) ? currentUser.pets.map(p => {
      const {
        name,
        type,
      } = p || {};
      return `${name} the ${type}`;
    }
    ).join(', ') : '';

    // console.log('lineItems', lineItems);

    let totalEstimate = 0;
    let payoutEstimate = 0;
    if (Array.isArray(lineItems)) {
      lineItems.forEach(item => {
        if (
          Array.isArray(item?.includeFor)
          && item.includeFor.includes('customer')
          && item.lineTotal
        ) {
          totalEstimate += convertMoneyToNumber(item.lineTotal);
        }
        if (
          Array.isArray(item?.includeFor)
          && item.includeFor.includes('provider')
          && item.lineTotal
        ) {
          payoutEstimate += convertMoneyToNumber(item.lineTotal);
        }
        if (
          !['line-item/provider-commission', 'line-item/gratuity'].includes(item.code)
          && item.days
        ) {
          onlyOneDay = parseInt(item.days || '0', 10) === 1 ? true : onlyOneDay;
        }
      });
    }

    const totalEstimateMoney = new Money(totalEstimate * 100, listing.attributes.price.currency);
    const payoutEstimateMoney = new Money(payoutEstimate * 100, listing.attributes.price.currency);
    
    const leadStartMoment = bookingMaybe?.bookingDates?.bookingStart ? moment.tz(bookingMaybe.bookingDates.bookingStart, 'America/Los_Angeles') : null;
    let leadEndMoment = bookingMaybe?.bookingDates?.bookingEnd ? moment.tz(bookingMaybe.bookingDates.bookingEnd, 'America/Los_Angeles').subtract(1, 'days') : null;
    let leadRangeFormattedShort = `${leadStartMoment ? leadStartMoment.format('M/D') : ''} - ${leadEndMoment ? leadEndMoment.format('M/D') : ''}`;

    // calculate days in between leadStart and leadEnd
    if (onlyOneDay) {
      const leadStart = bookingMaybe.bookingDates ? bookingMaybe.bookingDates.bookingStart.toISOString() : null;
      const leadEnd = bookingMaybe.bookingDates ? bookingMaybe.bookingDates.bookingEnd.toISOString() : null;
      if (leadStart && leadEnd) {
        const startMoment = moment.tz(leadStart, 'America/Los_Angeles');
        const endMoment = moment.tz(leadEnd, 'America/Los_Angeles');
        const hours = endMoment.diff(startMoment, 'hours');
        if (hours > 24) {
          onlyOneDay = false;
        }
      }
    }

    if (onlyOneDay && leadStartMoment) {
      leadEndMoment = leadStartMoment;
      leadRangeFormattedShort = leadStartMoment.format('M/D');
    }

    const formatTime = (timeStr) => {
      if (!timeStr) return '';
      const timeMoment = moment(`${timeStr}`, 'HH:mm');
      if (!timeMoment.isValid()) return timeStr;
      return timeMoment.format('h:mm A');
    }

    const leadData = {
      leadStart: bookingMaybe.bookingDates ? bookingMaybe.bookingDates.bookingStart.toISOString() : null,
      leadStartFormatted: leadStartMoment ? leadStartMoment.format('ddd, MMMM DD, YYYY') : '',
      // leadStartFormattedShort: leadStartMoment ? leadStartMoment.format('M/D') : '',
      leadEnd: bookingMaybe.bookingDates ? bookingMaybe.bookingDates.bookingEnd.toISOString() : null,
      leadEndFormatted: leadEndMoment ? leadEndMoment.format('ddd, MMMM DD, YYYY') : '',
      // leadEndFormattedShort: leadEndMoment ? leadEndMoment.format('M/D') : '',
      leadRangeFormattedShort,
      startTime: otherOrderData.startTime || '',
      startTimeFormatted: formatTime(otherOrderData?.startTime),
      endTime: otherOrderData.endTime || '',
      endTimeFormatted: formatTime(otherOrderData?.endTime),
      notes: message || otherOrderData.notes || '',
      serviceType: otherOrderData.serviceType || listing.attributes.title || '',
      location: location || otherOrderData.location || '',
      message: message, // Optional initial message
      gratuity: otherOrderData.gratuity ? parseFloat(otherOrderData.gratuity) : null,
      pets: pets,
      petText,
      totalEstimate: formatMoney(intl, totalEstimateMoney),
      payoutEstimate: formatMoney(intl, payoutEstimateMoney),
    };
    // console.log('leadData', leadData);

    // Use the duck function to send lead request
    return onSendLeadRequest(listing, leadData)
      .then(transactionId => {
        console.log('Lead request created:', transactionId);
        
        // Redirect to inbox to view the lead
        history.push(`/order/${transactionId.uuid}`);
        
        return transactionId;
      })
      .catch(error => {
        console.error('Error creating lead request:', error);
        // Error handling is done in the duck, just re-throw for any additional handling
        throw error;
      });

  } else {
    // Handle REGULAR BOOKING flow (existing logic)
    
    // priceVariantName is relevant for bookings
    const priceVariantNameMaybe = priceVariantName ? { priceVariantName } : {};
    const quantity = Number.parseInt(quantityRaw, 10);
    const quantityMaybe = Number.isInteger(quantity) ? { quantity } : {};
    const seats = Number.parseInt(seatsRaw, 10);
    const seatsMaybe = Number.isInteger(seats) ? { seats } : {};
    const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};

    const initialValues = {
      listing,
      orderData: {
        ...bookingMaybe,
        ...priceVariantNameMaybe,
        ...quantityMaybe,
        ...seatsMaybe,
        ...deliveryMethodMaybe,
        ...otherOrderData,
      },
      confirmPaymentError: null,
    };

    const saveToSessionStorage = !currentUser;

    // Customize checkout page state with current listing and selected orderData
    const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);

    callSetInitialValues(setInitialValues, initialValues, saveToSessionStorage);

    // Clear previous Stripe errors from store if there is any
    onInitializeCardPaymentData();

    // Redirect to CheckoutPage
    history.push(
      createResourceLocatorString(
        'CheckoutPage',
        routes,
        { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
        {}
      )
    );
  }
  
};

/**
 * Create fallback views for the ListingPage: LoadingPage and ErrorPage.
 * The PlainPage is just a helper for them.
 */
const PlainPage = props => {
  const { title, topbar, scrollingDisabled, children } = props;
  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={topbar} footer={<FooterContainer />}>
        {children}
      </LayoutSingleColumn>
    </Page>
  );
};

export const ErrorPage = props => {
  const { topbar, scrollingDisabled, invalidListing, intl } = props;
  return (
    <PlainPage
      title={intl.formatMessage({
        id: 'ListingPage.errorLoadingListingTitle',
      })}
      topbar={topbar}
      scrollingDisabled={scrollingDisabled}
    >
      <p className={css.errorText}>
        {invalidListing ? (
          <FormattedMessage id="ListingPage.errorInvalidListingMessage" />
        ) : (
          <span>
          {/* <FormattedMessage id="ListingPage.errorLoadingListingMessage" /> */}
          </span>
        )}
      </p>
    </PlainPage>
  );
};

export const LoadingPage = props => {
  const { topbar, scrollingDisabled, intl } = props;
  return (
    <PlainPage
      title={intl.formatMessage({
        id: 'ListingPage.loadingListingTitle',
      })}
      topbar={topbar}
      scrollingDisabled={scrollingDisabled}
    >
      <p className={css.loadingText}>
        <FormattedMessage id="ListingPage.loadingListingMessage" />
      </p>
    </PlainPage>
  );
};
